import { matchPath } from 'react-router-dom';

import type {
  AgentRunAttachment,
  DashboardRouteTarget,
} from '@superfunnel/types/agent-run';
import {
  pageEditorPath,
  quizEditorPath,
} from '@superfunnel/utils/connected-entity';

export const EDITOR_ROUTES = {
  globalSection: 'global-sections/:globalSectionId',
  quiz: 'quiz-funnels/:quizId/editor',
  embeddedQuiz: 'settings/embedded-forms/:quizId/editor',
  page: 'pages/:dynamicPageId/editor/:websiteId',
};

export const isDashboardEditorPath = (pathname: string) =>
  Object.values(EDITOR_ROUTES).some((route) =>
    matchPath(`/workspace/:slug/${route}`, pathname),
  );

export const DASHBOARD_ROUTE_TO_EVENT = 'superagent:route-to';

export interface DashboardRouteToEventDetail {
  target: DashboardRouteTarget;
  threadId: string | null;
}

const DASHBOARD_EDITOR_THREAD_ID_STATE = 'dashboardEditorThreadId';

const getRouteState = (state: unknown): Record<string, unknown> => {
  if (!state || typeof state !== 'object' || Array.isArray(state)) {
    return {};
  }

  return state as Record<string, unknown>;
};

export const getDashboardEditorThreadId = (
  state: unknown,
): string | undefined => {
  const threadId = getRouteState(state)[DASHBOARD_EDITOR_THREAD_ID_STATE];
  return typeof threadId === 'string' ? threadId : undefined;
};

export const withDashboardEditorThreadId = (
  state: unknown,
  threadId?: string,
): Record<string, unknown> => {
  const nextState = { ...getRouteState(state) };

  if (threadId) {
    nextState[DASHBOARD_EDITOR_THREAD_ID_STATE] = threadId;
  } else {
    delete nextState[DASHBOARD_EDITOR_THREAD_ID_STATE];
  }

  return nextState;
};

export interface DashboardEditorIntent {
  initialMessage?:
    string | { content: string; attachments?: AgentRunAttachment[] };
  initialShowAnalytics?: boolean;
  initialPreviewMode?: boolean;
}

const DASHBOARD_EDITOR_INTENT_STATE = 'dashboardEditorIntent';

export const getDashboardEditorIntent = (
  state: unknown,
): DashboardEditorIntent | undefined => {
  const intent = getRouteState(state)[DASHBOARD_EDITOR_INTENT_STATE];
  if (!intent || typeof intent !== 'object' || Array.isArray(intent)) {
    return undefined;
  }

  return intent as DashboardEditorIntent;
};

export const withDashboardEditorIntent = (
  state: unknown,
  intent?: DashboardEditorIntent,
): Record<string, unknown> => {
  const nextState = { ...getRouteState(state) };

  if (intent) {
    nextState[DASHBOARD_EDITOR_INTENT_STATE] = intent;
  } else {
    delete nextState[DASHBOARD_EDITOR_INTENT_STATE];
  }

  return nextState;
};

export const isDashboardRouteTarget = (
  value: unknown,
): value is DashboardRouteTarget => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const target = value as Partial<DashboardRouteTarget>;

  switch (target.type) {
    case 'path':
      return typeof target.path === 'string';
    case 'websiteEditor':
      return (
        typeof target.dynamicPageId === 'string' &&
        typeof target.websiteId === 'string'
      );
    case 'quizEditor':
      return (
        typeof target.quizId === 'string' &&
        (target.quizType === undefined ||
          target.quizType === 'independent' ||
          target.quizType === 'embedded')
      );
    case 'globalSectionEditor':
      return typeof target.globalSectionId === 'string';
    default:
      return false;
  }
};

type DashboardEditorTarget = Extract<
  DashboardRouteTarget,
  { type: 'websiteEditor' | 'quizEditor' }
>;

/** The path under `/workspace/{slug}` that opens a website or quiz editor. */
export const dashboardEditorPath = (target: DashboardEditorTarget): string =>
  target.type === 'websiteEditor'
    ? pageEditorPath(target.dynamicPageId, target.websiteId)
    : quizEditorPath(target.quizId, {
        formId: target.formId ?? undefined,
        embedded: target.quizType === 'embedded',
      });

export const dispatchDashboardRouteTo = (
  target: DashboardRouteTarget,
  threadId: string | null,
) => {
  window.dispatchEvent(
    new CustomEvent<DashboardRouteToEventDetail>(DASHBOARD_ROUTE_TO_EVENT, {
      detail: { target, threadId },
    }),
  );
};
